import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, User, BookOpen, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ODISHA_DISTRICTS } from "../lib/districts";
import { Chip, PageHero } from "../components/ui-parts";
import { useI18n } from "../contexts/I18nContext";

export default function CourseDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ full_name: "", mobile: "", email: "", age: "", district: "", state: "Odisha", address: "", preferred_mode: "", message: "" });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/courses/${id}`).then(r => setC(r.data)); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/enrollments", { ...form, course_id: id, age: form.age ? parseInt(form.age) : null });
      setDone(data.ref);
      toast.success(`Enrolled! Reference: ${data.ref}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrollment failed");
    } finally { setSubmitting(false); }
  };

  if (!c) return <div className="section container-narrow">Loading…</div>;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="course-detail-page">
      <PageHero badge={c.is_free ? t("common.free") : t("common.paid")} title={c.name} subtitle={c.description} />
      <section className="section">
        <div className="container-narrow grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-[var(--js-primary)] mb-6"><ArrowLeft size={14} /> All courses</Link>
            <div className="flex flex-wrap gap-2 mb-6">
              <Chip variant={c.is_free ? "free" : "paid"}>{c.is_free ? t("common.free") : `₹${c.fee || "TBD"}`}</Chip>
              <Chip>{c.mode}</Chip>
              <Chip>{c.level}</Chip>
              <Chip>{c.duration}</Chip>
            </div>
            <div className="js-card p-6 mb-4"><h3 className="font-heading text-lg text-[var(--js-primary)] mb-2">Syllabus</h3><p className="text-[var(--js-muted)] whitespace-pre-line">{c.syllabus}</p></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="js-card p-4 text-sm"><User size={14} className="inline mr-1" /> <b>Instructor:</b> {c.instructor}</div>
              <div className="js-card p-4 text-sm"><MapPin size={14} className="inline mr-1" /> <b>District:</b> {c.district}</div>
              <div className="js-card p-4 text-sm"><Clock size={14} className="inline mr-1" /> <b>Start:</b> {c.start_date}</div>
              <div className="js-card p-4 text-sm"><BookOpen size={14} className="inline mr-1" /> <b>Eligibility:</b> {c.eligibility}</div>
            </div>
          </div>

          <div className="js-card p-6 h-fit sticky top-24" data-testid="enroll-form">
            <h3 className="font-heading text-xl text-[var(--js-primary)] mb-4">{t("common.enroll")}</h3>
            {done ? (
              <div className="text-center py-6"><Check className="mx-auto text-[var(--js-accent)]" size={32} /><p className="mt-3 font-semibold">Registered!</p><p className="text-sm text-[var(--js-muted)] mt-1">Reference:</p><p className="font-mono text-[var(--js-primary)] mt-1">{done}</p></div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                {[["full_name", "Full Name"], ["mobile", "Mobile"], ["email", "Email"], ["age", "Age"], ["address", "Address"]].map(([k, l]) => (
                  <input key={k} required={k !== "age" && k !== "address"} type={k === "email" ? "email" : k === "age" ? "number" : "text"} placeholder={l} value={form[k]} onChange={set(k)} data-testid={`enroll-${k}`} className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" />
                ))}
                <select required value={form.district} onChange={set("district")} data-testid="enroll-district" className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm">
                  <option value="">Select District</option>
                  {ODISHA_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select value={form.preferred_mode} onChange={set("preferred_mode")} data-testid="enroll-mode" className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm">
                  <option value="">Preferred Mode</option>
                  <option>Online</option><option>Offline</option><option>Hybrid</option>
                </select>
                <textarea placeholder="Message" value={form.message} onChange={set("message")} data-testid="enroll-message" className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm h-20" />
                <button disabled={submitting} className="js-btn-primary w-full justify-center" data-testid="enroll-submit">{submitting ? "…" : t("common.submit")}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
